export interface RosterInput { rotation: number; weServe: boolean; libero?: boolean }
export type LiberoState = { onCourt: false; replaces: null; reason: "noLibero" | "liberoMayNotServe" } | { onCourt: true; replaces: string; reason: null };
/**
 * Libero replacements, from FIVB 2025-2028 Rule 19.
 *
 *   19.3.1.1  the Libero is allowed to replace any player in a back row position
 *   19.3.1.3  he/she may not serve, block or attempt to block
 *
 * In a 5-1 the two middles stand three zones apart, so exactly one of them is
 * always in the back row and the libero has an obvious partner in every
 * rotation. The one wrinkle: when that middle rotates to zone 1 and our team
 * wins the serve, the middle has to come back on to serve, because a libero
 * never may. That happens in rotations 3 and 6.
 *
 * A team can also play without one at all — plenty do — in which case both
 * middles play the whole way round and nobody is ever substituted.
 */

import { isFrontRow, slotsFor } from "./rotation.js";

/** The middle blocker standing in the back row in a given rotation. */
export function backRowMiddle(rotation: number) {
  const slots = slotsFor(rotation);
  const middle = ["MB1", "MB2"].find((role) => !isFrontRow(slots.get(role)!));
  if (!middle) throw new Error(`no back-row middle in rotation ${rotation}`);
  return middle;
}

/**
 * Whether the libero is on court, and for whom.
 *
 * @returns `{ onCourt, replaces, reason }` — `replaces` is the middle whose
 *          place the libero has taken, or null when the libero is off court.
 */
export function liberoStateFor({ rotation, weServe, libero = true }: RosterInput): LiberoState {
  if (!libero) {
    return { onCourt: false, replaces: null, reason: "noLibero" };
  }

  const middle = backRowMiddle(rotation);
  const middleZone = slotsFor(rotation).get(middle);
  const middleWouldServe = weServe && middleZone === 1;

  if (middleWouldServe) {
    return { onCourt: false, replaces: null, reason: "liberoMayNotServe" };
  }
  return { onCourt: true, replaces: middle, reason: null };
}

/**
 * Who is actually standing on court, keyed by role.
 *
 * The libero appears as the role `"L"` in the zone of the middle they replaced,
 * and that middle is reported as `benched` and left out of the slot map, so the
 * map always holds exactly six players.
 */
export function rosterFor({ rotation, weServe, libero = true }: RosterInput) {
  const slots = slotsFor(rotation);
  const state = liberoStateFor({ rotation, weServe, libero });

  if (!state.onCourt) {
    return { slots, benched: null, libero: state };
  }

  const zone = slots.get(state.replaces)!;
  const onCourt = new Map(slots);
  onCourt.delete(state.replaces);
  onCourt.set("L", zone);

  return { slots: onCourt, benched: state.replaces, libero: state };
}
